
import { Logger }          from '@/util/logger';

import {
  ParserErrorCallback,
  ParserWarningCallback,
  BufferCompletedCallback,
  EventType,
  ParserEvent, Listener
} from './e.m.c.a.404.events';
import { Buffer }          from './e.m.c.a.404.types';
import {
  Frame,
  FrameType
}                          from './frame';
import {EventTree}         from "./event.tree";

const BRACE_OPEN: number     = '{'.charCodeAt(0);
const BRACE_CLOSE: number    = '}'.charCodeAt(0);
const BRACKET_OPEN: number   = '['.charCodeAt(0);
const BRACKET_CLOSE: number  = ']'.charCodeAt(0);
const COMMA: number          = ','.charCodeAt(0);
const COLON: number          = ':'.charCodeAt(0);
const QUOTE: number          = '"'.charCodeAt(0);
const SPACE: number          = ' '.charCodeAt(0);
const NEWLINE: number        = '\n'.charCodeAt(0);
const CARRIAGERETURN: number = '\r'.charCodeAt(0);
const TAB: number            = '\t'.charCodeAt(0);
const BACKSLASH: number      = '\\'.charCodeAt(0);

/**
 *
 * @class EMCA404
 *
 * @brief a combined lexical stage and parser for JSON.  This parser
 * is designed to work in a progressive fashion; start with a buffer,
 * parse as much as will parse in a syntactically correct way, and then
 * ask for more input.  When input arrives, we keep parsing, prepending
 * whatever was left over from last time.
 *
 * The expectation is that blocks (buffers) of data will be coming from
 * a JSON files being read block by block (in order) until the file is
 * completely read (and completely parsed).
 *
 * The motivation for doing buffered parsing is that sometimes we are
 * given very large JSON files in the 25 - 50MB range (potentially
 * even larger).  So we need to be able to do JSON parsing and the
 * building of our internal models based on resulting objects)... in the
 * background.  So we aren't "locking up the GUI" while we choke down
 * this massive JSON file.
 *
 * Usage: Construct an instance, it will then store the state of parsing,
 * the object tree etc.  Once constructed, just feed it the blocks of
 * text (in order) from the source file, by calling the parse() method
 * with each block.
 *
 * To track what is happening, use the event handlers.
 *
 * Some ideas for the parsing taken from:
 *
 *   - https://github.com/alemures/fast-json
 *   - https://datastation.multiprocess.io/blog/2022-01-06-analyzing-large-json-files-via-partial-json-parsing.html
 *   - https://github.com/uhop/stream-json
 *   - https://github.com/Collaborne/incremental-json-parser
 *   - https://djedr.github.io/posts/jsonhilo-2021-07-29.html
 *   - https://github.com/xtao-org/jsonhilo
 *
 * Reading on JSON itself:
 *
 *   - https://www.json.org/json-en.html
 *   - https://datatracker.ietf.org/doc/html/rfc8259
 *   - https://www.ecma-international.org/wp-content/uploads/ECMA-404_2nd_edition_december_2017.pdf
 *   - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON
 *
 * JSON Lint
 *
 *   - https://github.com/jslint-org/
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */
export class EMCA404 {

  /** unique ids for events */
  private static eventId: number   = 0;

  /** logical line number */
  private line: number             = 1;

  /** column of logical line */
  private col: number              = 1;

  /** the byte position within the current buffer */
  private cursor:  number          = 0;

  /** the listeners for general events around managing the parsing */
  private basicEvents : Map<string, (BufferCompletedCallback|ParserWarningCallback|ParserErrorCallback)[]> =
    new Map<string, (BufferCompletedCallback|ParserWarningCallback|ParserErrorCallback)[]>();

  /** the current buffer we are parsing */
  private buffer: Buffer           = '';

  /** the current buffer start position in the accumulated string */
  private bufferStart: number      = 0;

  /** the accumulated raw string so far */
  private raw: string              = '';

  /** flag for trying to scan past the end of the buffer */
  private eob: boolean             = false;

  /** our depth first build out of the object tree. */
  private stack: Frame[]           = [];

  private events: EventTree;

  /** where the most recent string started */
  private stringStart: number      = 0;

  /** where the most recent string ended */
  private stringEnd: number        = 0;

  private postColon: boolean;

  /** when we are skipping, we just match brace or bracket until we've skipped a block. */
  private skipping: boolean       = false;

  /** in skipping mode this is the block opening character */
  private skipStartChar: number   = 0;

  /** in skipping mode this is the block closing character */
  private skipEndChar: number     = 0;

  /** where the skip started */
  private skipStart: number       = 0;

  /** where the skip ended */
  private skipEnd: number         = 0;

  /** we might be skipping across buffers, so we have to remember our depth */
  private skipDepth: number       = 0;

  private skipStringStart: number = 0;

  private skipStringEnd: number   = 0;

  /** we might be skipping across buffers, so we have to remember if we are currently skipping a string */
  private skipString: boolean     = false;

  /** the start of the most recent primitive */
  private primitiveStart: number  = 0;

  /** the end of the most recent primitive */
  private primitiveEnd: number    = 0;

  /** we might be in the middle of scanning a primitive when switching buffers */
  private scanPrimitive: boolean  = false;

  private openQuote: boolean      = false;

  /** standard constructor */
  constructor() {

    this.postColon = false;
    this.events    = new EventTree();

    //Logger.debug(`[EMCA404] ready to parse.`);
  }

  /** fetch the raw string parsed so far */
  public rawString(): string {

    return this.raw;
  }

  /**
   * register an event listener for a specific kind of event
   *
   * @param tag      the kind of event to listen on
   * @param callBack the listener
   *
   * @private
   */
  private addBasicListener(
    tag: string,
    callBack: BufferCompletedCallback|ParserWarningCallback|ParserErrorCallback): void {

    if (this.basicEvents.has(tag)) {

      // @ts-ignore
      this.basicEvents.get(tag).push(callBack);

    } else {

      /* first listener of this type */

      this.basicEvents.set(tag, [callBack]);
    }

  }

  /**
   * send basic events out to any listeners
   *
   * @param tag  the kind of event
   * @param data the data to publish
   *
   * @private
   */
  private emitBasic(tag: string, data?: any) {

    EMCA404.eventId++;

    let event: ParserEvent = {
      id: EMCA404.eventId,
      tag,
      data,
    };

    if (!this.basicEvents.has(tag)) {

      /* no listeners, nothing to do */

      return ;
    }

    // @ts-ignore
    let listeners: (BufferCompletedCallback|ParserWarningCallback|ParserErrorCallback)[] =
      this.basicEvents.get(tag);

    listeners.forEach((callBack) => {

      callBack(event);

    });
  }

  /** fetch the nth character of the given buffer (or default to current buffer). */
  private charAt(index: number, data?: Buffer): number {

    if (!data) {

      data = this.buffer;
    }

    return data.charCodeAt(index);
  }

  /** try to match up to end of the current block, and just ignore the internal text. */
  private skip(start: number): number {

    let parser: EMCA404    = this;
    let i: number          = start;

    /*
     * just assume the JSON is syntactically correct, and find the matching
     * brace or bracket. If we consume the entire buffer...then we stay in
     * skip mode, even across buffers, until we match or don't (end of file)
     *
     */

    for (; parser.skipDepth > 0; i++) {

      if (i >= parser.buffer.length) {

        /* we've exhausted the current buffer, we need another one! */

        parser.eob    = true;
        parser.cursor = parser.buffer.length;

        return parser.cursor;
      }

      let c: number = parser.charAt(i);

      /*
       * if we're already in skip mode, then we want to jump straight into that.
       * so set 'c' to be QUOTE, so we fall directly into that mode.
       *
       */

      if (parser.skipString) {

        c = QUOTE;
      }

      switch (c) {

        case QUOTE: {

          /* skip the string, so we don't false match on characters inside the string */

          let j: number = i + 1; /* ignore the starting quote! */

          if (parser.skipString) {

            /* we aren't actually at the start quote, we faked our way in above */

            j--;

          } else {

            parser.skipString = true;
            parser.skipStringStart = parser.bufferStart + i;
          }

          let found: boolean = false;

          for (; j<parser.buffer.length; j++) {

            switch (parser.charAt(j)) {

              case QUOTE:

                found             = true;

                break;

              case BACKSLASH:

                j++;

                break;

              default:
            }

            if (found) {

              /* we matched the end of string...within a block we are skipping */

              parser.skipString    = false;
              parser.skipStringEnd = parser.bufferStart + j + 1;

             // Logger.debug(`[EMCA404] skip - string skipped=`, parser.raw.slice(parser.skipStringStart, parser.skipStringEnd));

              break;

            }

          } /* end of string skip loop */

          /* did we exhaust the buffer? */

          if (j >= parser.buffer.length) {

            parser.eob    = true;
            parser.cursor = parser.buffer.length;

            return parser.cursor;
          }

          i = j;

          break;
        }

        case parser.skipStartChar:

          /* its nested */

          parser.skipDepth++;

          break;

        case parser.skipEndChar:

          /* closed a child block */

          parser.skipDepth--;

          break;

        default:
      }

    } /* end of close block matching */

    /* we matched the end of the block! */

    i--;

    parser.skipEnd = this.bufferStart  + i + 1;

    //Logger.debug(`[EMCA404] skip - block skipped=`, parser.raw.slice(parser.skipStart, parser.skipEnd));

    parser.skipping        = false;
    parser.skipStartChar   = 0;
    parser.skipEndChar     = 0;
    parser.skipDepth       = 0;
    parser.skipString      = false;
    parser.skipStringStart = 0;
    parser.skipStringEnd   = 0;

    return i;
  }

  /** fetch the top of the stack */
  private frame(): Frame|null {

    if (this.stack.length === 0) {

      /* no stack yet. */

      return null;
    }

    return this.stack[this.stack.length - 1];
  }

  /** fetch the last quoted string we parsed. */
  private lastString(): string {

    return this.raw.slice(this.stringStart, this.stringEnd);
  }

  /** construct a node key */
  private key(): string {

    let frame:Frame|null = this.frame();

    if (!frame) {

      /* no stack / no tree...yet */

      return '/';
    }

    /* if we just opened an array, the node key is the index into the array */

    if (frame.isArray()) {

      return frame.currentIndex().toString();
    }

    /*
     * if we just opened an object, the array key will be the most
     * recent string; i.e. a field name.  if the root is an object,
     * then its handled above (the key is just the path separator).
     *
     */

    return this.lastString();
  }

  /** start a new frame for a new object or array */
  private onOpenBlock(start: number, isObject: boolean, startChar: number, endChar: number): number {

    //Logger.debug(`[EMCA404] onOpenBlock - opening a new block...`);

    let parser: EMCA404 = this;

    /*
     * establish the key to use for this block; if it's an object there would have been a
     * a field name preceding us, or if we are inside an array, this object is at some
     * index position.
     *
     */

    const key = this.key();

    /* if there is no watcher for this node...we don't have to parse it, just skip it entirely. */

    if (!this.events.hasNode(key)) {

      /* drop into skipping mode, we won't even be adding this whole subtree */

      parser.skipping        = true;
      parser.skipStartChar   = startChar;
      parser.skipEndChar     = endChar;
      parser.skipDepth       = 1;
      parser.skipString      = false;

      parser.skipStringStart = 0;
      parser.skipStringEnd   = 0;

      parser.skipStart       = this.bufferStart + start;
      parser.skipEnd         = 0;

      return start;
    }

    /* move the event matcher cursor down the path tree to this node */

    this.events.down(key);

    /* push a new frame for this block */

    this.stack.push(new Frame({
      kind: isObject ? FrameType.OBJECT : FrameType.ARRAY,
      start: parser.bufferStart + start,
      end: 0,
      key,
      index: 0,
      field: parser.lastString().replaceAll('"', '')
    }));

    this.postColon = false;

    /* now continue parsing inside the block, essentially recurse */

    return start;
  }

  /** fetch the current position's JSON path */
  private path() {

    if (this.stack.length == 0) {

      return '/';
    }

    // @ts-ignore
    let frame: Frame = this.stack[this.stack.length - 1];

    let blockPath: string = '';

    this.stack.forEach((frame: Frame) => {

      if (blockPath.length == 0) {

        blockPath += frame.key;

      } else {

        blockPath += frame.key + '/';
      }
    });

    return blockPath.slice(0, blockPath.length - 1).replace(/\/$/, '');
  }

  /** complete a frame for an object or an array */
  private onCloseBlock(end: number): void {

    let parser: EMCA404 = this;

    //Logger.debug(`[EMCA404] onCloseBlock - completed block.`);

    if (parser.stack.length === 0) {

      /* nothing to do */

      return ;
    }

    // @ts-ignore
    let frame: Frame = parser.stack[parser.stack.length - 1];

    frame.end = parser.bufferStart + end;

    let blockPath: string = parser.path();

    // @ts-ignore
    frame = parser.stack.pop();

    this.postColon = false;

    if (parser.events.hasListener()) {

      /* send out the even for this object/array in the JSON */

      //Logger.debug(`[EMCA404] onCloseBlock - emitting block...`);

      parser.events.emit(parser.raw.slice(frame.start, frame.end + 1), blockPath);
    }

    /* move the event matcher cursor back up to the parent */

    parser.events.up();
  }

  /** parse a string value */
  private onQuote(): number {

    //Logger.debug(`[EMCA404] onQuote - scanning a string...`);

    let parser: EMCA404 = this;

    if (!this.openQuote) {

      /* ? we aren't scanning a string right now. */

      return parser.cursor;
    }

    /* find the end of the string */

    let c: number      = 0;
    let found: boolean = false;

    for (; (parser.stringEnd < parser.buffer.length) && !found; parser.stringEnd++) {

      c = this.charAt(parser.stringEnd);

      switch (c) {

        case QUOTE:

          found = true;

          break;

        case BACKSLASH:

          parser.stringEnd++;

          break;

        default:
      }

      if (found) {

        break;
      }

    }

    let cursor: number = parser.stringEnd;

    /* whether we completed a string or not, are we at the end of the buffer? */

    if ((parser.stringEnd + 1) >= parser.buffer.length) {

      /* scanned past the end of the buffer, we have to pause */

      parser.eob       = true;

     // Logger.debug(`[EMCA404] onQuote - reached end of buffer...`);

    }

    /* did we complete a string? */

    if (c == QUOTE) {

      parser.stringEnd = parser.bufferStart + parser.stringEnd + 1;

      //Logger.debug(`[EMCA404] onQuote - string completed.`);

      //Logger.debug(`[EMCA404] onQuote - emitting string...`);

      this.emitPrimitive(this.stringStart, this.stringEnd);

      parser.postColon = false;
      parser.openQuote = false;

    }  /* past end of the buffer? */

    return cursor;
  }

  /** useful for housekeeping, tracking how var we are into an object or an array */
  private onComma(): void {

    if (this.stack.length === 0) {

      /* no stack yet */

      return ;
    }

    // @ts-ignore
    let frame: Frame = this.frame();

    if (frame.isArray()) {

      /*
       * if we are working our way through an array, each comma means we are working on
       * a new element.
       *
       */

      frame.index++;
    }

  }

  /** scan a litteral; number or boolean */
  private onPrimitive(start: number): number {

    //Logger.debug(`[EMCA404] onPrimitive - scanning a primitive...`);

    let parser: EMCA404 = this;

    if (!this.scanPrimitive) {

      /* ? we aren't scanning a primitive right now. */

      return parser.cursor;
    }

    /* try to find the end of the primitive */

    let found: boolean  = false;

    for (; parser.primitiveEnd < (parser.buffer.length) && !found; parser.primitiveEnd++) {

      switch (parser.charAt(parser.primitiveEnd)) {

        case BRACKET_CLOSE:
        case BRACE_CLOSE:
        case COMMA:
        case TAB:
        case CARRIAGERETURN:
        case NEWLINE:
        case SPACE:
          found = true;
          break;

        default:
      }

      if (found) {
        break;
      }
    }

    let cursor: number = parser.primitiveEnd - 1;

    /* whether we completed a string or not, are we at the end of the buffer? */

    if ((parser.primitiveEnd) >= parser.buffer.length) {

      /* scanned past the end of the buffer, we have to pause */

      parser.eob       = true;

      //Logger.debug(`[EMCA404] onPrimitive - reached end of buffer...`);

    }

    /* did we complete a primitive? */

    if (found) {

      /* its not a string, don't include the characters we stopped on (, } etc.) */

      parser.primitiveEnd  = parser.bufferStart + parser.primitiveEnd;
      parser.scanPrimitive = false;

      //Logger.debug(`[EMCA404] onPrimitive - completed.`);

      //Logger.debug(`[EMCA404] onPrimitive - emitting ...`);

      parser.scanPrimitive = false;

      this.emitPrimitive(this.primitiveStart, this.primitiveEnd);

    }  /* past end of the buffer? */

    return cursor;
  }

  /** advance the cursor to the new position, but only if it's not past the end of the buffer */
  private advance(position: number): number {

    let parser: EMCA404 = this;

    /* if no buffer, then cursor position should always be 0 */

    if (!parser.buffer) {

      return 0;
    }

    /*
     * if trying to move past end the buffer, then we have to pause, we need
     * another buffer!
     *
     */

    if (position >= parser.buffer.length) {

      parser.eob = true;
    }

    /* ok, this is a normal forward movement */

    parser.cursor = position;

    return parser.cursor;
  }

  private emitPrimitive(
    start: number,
    end: number
  ): void {

    //Logger.debug(`[EMCA404] emitPrimitive - text=`, this.raw.slice(start, end));

    /* if we just moved past a ":" then we also just moved past a field name. */

    /*
     * if we are currently parsing an array, we can emit an event
     * for this element...if there is a listener for it.
     *
     */

    let frame: Frame|null = this.frame();

    if (!frame) {

      /* no open array or object */

      return ;
    }

    if (this.postColon) {

      const key = frame.currentField();

      /*
       * fields/elements of arrays, don't have a frame on the stack, but
       * we can temporarily move the cursor down in the matching tree,
       * fire the event if needed, then immediately return the cursor back
       * to its current position, so It's still tracking the current open
       * node in the JSON parsing.
       *
       */

      if (!this.events.hasNode(key)) {

        /* no watchers on the fields of this object */

        return ;
      }

      this.events.down(key);

      /* is there even a watcher for this primitive? */

      if (this.events.hasListener()) {

        /* send the event */

        this.events.emit(this.raw.slice(start, end), this.path() + '/' + key);
      }

      this.events.up();

      return;
    }

    if (!frame.isArray()) {

      /* not currently parsing through an array */

      return ;
    }

    let key: string = frame.currentIndex().toString();

    if (!this.events.hasNode(key)) {

      /* no watchers on the elements of this array */

      return ;
    }

    if (this.events.hasNode(key)) {

      /*
       * similar to fields of objects, there is no stack frame for each
       * element of an array, just temporarily move the event matching
       * cursor down to the element node, and then immediately back up
       * afterwards.
       *
       */

      this.events.down(key);

      if (this.events.hasListener()) {

        this.events.emit(this.raw.slice(start, end), this.path() + '/' + frame.currentIndex());
      }

      this.events.up();
    }
  }

  /**
   * start parsing a new buffer, picking up where the last one ended.
   *
   * @param buffer the next buffer to parse, buffers should be given
   * from the source file in order.
   *
   */
  public parseBuffer(buffer: Buffer): void {

    let parser: EMCA404 = this;

    //Logger.debug(`[EMCA404] parseBuffer - starts...`);

    /* track the accumulated total string */

    parser.bufferStart  = parser.raw.length;
    parser.raw         += buffer;

    /* if we didn't read the last buffer's final bytes, we need to prepend those first */

    if (parser.buffer && parser.cursor < parser.buffer.length) {

      /*
       * there are some bytes left in the previous buffer which were not consumed, grab
       * those and prepend to the incoming buffer so that we seamlessly move from one
       * buffer to the next.
       */

      let c: string = parser.buffer.slice(parser.cursor);

      //Logger.debug(`[EMCA404] parseBuffer - including previous unparsed text: `, c);

      /*
       * don't forget to roll back the buffer start position, because we didn't actually
       * use all of the previous buffer.
       *
       */

      parser.bufferStart -= c.length;

      c += buffer;

      buffer = c;

    }

    /* reset for parsing this buffer */

    parser.cursor = 0;
    parser.eob    = false;

    /* ok, we can start parsing the buffer */

    parser.buffer = buffer;

    //Logger.debug(`[EMCA404] parseBuffer - moving to new buffer, sz=${buffer.length}`);

    /*
     * the character state machine ...
     *
     * until we absolutely have to actually do JSON parsing to JavaScript objects...don't.
     * Everything is treated as an outline, a start/end position for the substring that
     * would parse to something valid with JSON.parse().
     *
     * For the sake of finding the stuff to treat as objects we can pass back to matching
     * paths (so the application can get the data) though, all we need is substrings and
     * their ranges...which is faster to find than trying to do full parsing and object
     * creation.
     *
     * So basically our loop is about finding all the interesting sub-ranges of the JSON.
     * We don't care about it being unicode or not or need to do detailed lint style
     * checking...we just need the sub-ranges.
     *
     * We can build up the "tree" of sub-ranges by just using a depth first stack.  When
     * we see a '{' push a new frame.  When we see a '}' pop it and do whatever housekeeping,
     * to send the data back to the application.  It's at that point, if we find a match
     * for what the application is looking to gather, and what we actually have...that we
     * can invoke JSON.parse() to give them a fully converted javascript object.
     *
     */

    for (; parser.cursor < parser.buffer.length && !parser.eob; parser.cursor++) {

      /*
       * if we are in string gathering mode, then force 'c' to be a quote so that we drop
       * directly back into string gathering mode. This should only happen when a string is
       * spanning one or more buffers.
       *
       */

      if (parser.openQuote) {

        parser.stringEnd = parser.cursor;

        //Logger.debug(`[EMCA404] parseBuffer - continuing to parse string...`);

        parser.advance(this.onQuote());

        /*
         * if we matched the primitive or not, or if at the end of the buffer,
         * or not, all the same...go back to the top of the loop and let the normal
         * logic resume.
         *
         */

        continue;
      }

      /*
       * if we are in skipping mode, the just quickly scan through the current object/array
       * this allows us to ignore large chunks of text that we aren't interested in.
       *
       */

      if (parser.skipping) {

        //Logger.debug(`[EMCA404] parseBuffer - continuing to skip...`);

        parser.advance(parser.skip(parser.cursor));

        if (parser.eob) {

          /* didn't match the end of the block in the current buffer, we need another one! */

          /*
           * we don't leave the cursor at the last valid point, we're skipping, so we're
           * ignoring all the text inside the current block.  Just jump to teh very end
           * and let the next buffer attempt to match up (close out) this block.
           *
           */

          parser.cursor = parser.buffer.length;

        }

        continue;
      }

      if (parser.scanPrimitive) {

        //Logger.debug(`[EMCA404] parseBuffer - continuing to gather primitive...`);

        parser.primitiveEnd = parser.cursor;

        parser.advance(parser.onPrimitive(parser.cursor));

        /*
         * if we matched the primitive or not, or if at the end of the buffer,
         * or not, all the same...go back to the top of the loop and let the normal
         * logic resume.
         *
         */

        continue;
      }

      let c: number = parser.charAt(parser.cursor);

      switch (c) {

        case BRACE_OPEN:

          /* we're starting an object */

          parser.advance(this.onOpenBlock(
            parser.cursor,
            true,
            BRACE_OPEN,
            BRACE_CLOSE
          ));
          break;

        case BRACKET_OPEN:

          /* we're starting an array */

          parser.advance(this.onOpenBlock(
            parser.cursor,
            false,
            BRACKET_OPEN,
            BRACKET_CLOSE
          ));
          break;

        case BRACE_CLOSE:
        case BRACKET_CLOSE:

          /* we've completed an object or an array */

          this.onCloseBlock(parser.cursor);

          break;

        case CARRIAGERETURN:
        case NEWLINE:

          /* look for line endings, so we can track the line number */

          let lineEnd: string = parser.buffer.slice(parser.cursor, parser.cursor + 2);

          if (lineEnd.charAt(0) == '\r') {

            if (lineEnd.charAt(1) == '\n') {

              parser.line++;

              parser.col = 1;

              /* eat the \n, so we don't double count it */

              parser.cursor++;
            }

          } else if (lineEnd.charAt(0) == '\n') {

            parser.line++;

            parser.col = 1;

            if (lineEnd.charAt(1) == '\r') {

              /* eat the \r, so we don't double count it */

              parser.cursor++;
            }
          }

          break;

        case TAB:
        case SPACE:

          /* ignore whitespace, or use for bookkeeping between elements */

          break;

        case QUOTE:

          /* passthrough a string */

          parser.stringStart = parser.bufferStart + parser.cursor;
          parser.stringEnd   = parser.cursor + 1;
          parser.openQuote   = true;

          parser.advance(this.onQuote());

          break;

        case COLON:

          /* after a colon we expect a value */

          {
            this.postColon = true;

            let frame: Frame | null = this.frame();

            if (frame) {

              /* once we see the ':' we know the last string was the most recent field name */

              frame.field = this.lastString();
            }
          }

          break;

        case COMMA:

          /* field or value separator */

          this.onComma();

          /*
           * if we are inside an object, and we see the ',', it means we're about
           * to start a new field.  So reset postColon to false.
           *
           */

          {

            let frame: Frame | null = this.frame();

            if (frame) {

              if (frame.isObject()) {

                /* completed the current object field */

                this.postColon = false;
                frame.field    = '';
              }
            }
          }
          break;

        default:

          /* for anything else, it better be a primitive like a bool or number */

          parser.primitiveStart = parser.bufferStart + parser.cursor;
          parser.primitiveEnd   = parser.cursor;
          parser.scanPrimitive  = true;

          parser.cursor = parser.advance(this.onPrimitive(parser.cursor));

      } /* end of switch */

    } /* end of for loop */

    /* have we exhausted the buffer? */

    if (parser.cursor >= parser.buffer.length) {

      parser.eob = true;

      //Logger.debug(`[EMCA404] parseBuffer - end of buffer reached.`);

    } else {

      /* this method will be called again, on the next buffer ... */

      //Logger.debug(`[EMCA404] parseBuffer - buffer is partially parsed, need a new buffer.`);
    }

    /* let application know we need another buffer */

    parser.emitBasic(EventType.BUFFER_COMPLETED);
  }

  /**
   * register to listen for failure
   *
   * @param callBack your function to call for this event.
   */
  public onError(callBack: ParserErrorCallback): EMCA404 {

    this.addBasicListener(EventType.ERROR, callBack);

    return this;
  }

  /**
   * register to listen for failure
   *
   * @param callBack your function to call for this event.
   */
  public onWarning(callBack: ParserWarningCallback): EMCA404 {

    this.addBasicListener(EventType.WARNING, callBack);

    return this;
  }

  /**
   * register to listen for failure
   *
   * @param callBack your function to call for this event.
   */
  public onBufferCompleted(callBack: BufferCompletedCallback): EMCA404 {

    this.addBasicListener(EventType.BUFFER_COMPLETED, callBack);

    return this;
  }

  /**
   * register a callback for the given JSON object path.  You can use
   * basic Javascript like syntax.  For example:
   *
   *   user/first.name
   *
   * to listen for when the first.name field is pared in the object user.  You
   * can use [] for array elements and you can use a glob character (*) for
   * field names or for array indexes.  more examples:
   *
   *   a/a[3][0]
   *   b[0]/c
   *   aa/*
   *
   * Keep in mind the glob character (*) is not recursive, it is only applied to
   * the level where you use it.
   *
   * @param path
   * @param listener
   */
  on(path: string, listener: Listener): void {

    //Logger.debug(`[EMCA404] registering JSON handler for: ${path} ...`);

    this.events.on(path, listener);
  }

}

